import { ChainId, ProviderType } from '@dcl/schemas';
import { ethers } from 'ethers';
import { io } from 'socket.io-client';
import { Authenticator, AuthLinkType } from '@dcl/crypto';
import * as sso from '@dcl/single-sign-on-client';
import { getRpcUrls } from '../configuration';
const STORAGE_KEY_ADDRESS = 'auth-server-provider-address';
const STORAGE_KEY_CHAIN_ID = 'auth-server-provider-chain-id';
function isRPCError(outcome) {
    return (typeof outcome === 'object' &&
        outcome !== undefined &&
        outcome !== null &&
        'message' in outcome &&
        'code' in outcome);
}
function isErrorWithMessage(error) {
    return (typeof error === 'object' &&
        error !== undefined &&
        error !== null &&
        'message' in error);
}
export class AuthServerProvider {
    constructor() {
        this.getChainId = () => {
            const chainId = localStorage.getItem(STORAGE_KEY_CHAIN_ID);
            if (!chainId) {
                return ChainId.ETHEREUM_MAINNET;
            }
            return Number(chainId);
        };
        this.getAccount = () => {
            return AuthServerProvider.getAccount();
        };
        this.request = async ({ method, params }) => {
            // The chain id is a virtual concept in this provider.
            // Changing it will only affect the rpc used for eth_calls and other non-transactional calls.
            // It will also affect the result value of the eth_chainId and net_version calls.
            if (method === 'wallet_switchEthereumChain') {
                const chainId = parseInt(params[0].chainId, 16).toString();
                localStorage.setItem(STORAGE_KEY_CHAIN_ID, chainId);
                return undefined;
            }
            if (['eth_chainId', 'net_version'].includes(method)) {
                return this.getChainId();
            }
            if (['eth_accounts', 'eth_requestAccounts'].includes(method)) {
                const account = this.getAccount();
                if (!account) {
                    return [];
                }
                else {
                    return [account];
                }
            }
            /**
             * These ethereum calls don't require the user's wallet given that no new transaction or signature is being created.
             * Because of this, we can use a regular provider to make the call, without the need of opening the auth dapp.
             */
            if ([
                'eth_getTransactionReceipt',
                'eth_estimateGas',
                'eth_call',
                'eth_getBalance',
                'eth_getStorageAt',
                'eth_blockNumber',
                'eth_gasPrice',
                'eth_protocolVersion',
                'net_version',
                'web3_sha3',
                'web3_clientVersion',
                'eth_getTransactionCount',
                'eth_getBlockByNumber',
                'eth_getCode'
            ].includes(method)) {
                const provider = new ethers.providers.JsonRpcProvider(getRpcUrls(ProviderType.AUTH_SERVER)[this.getChainId()]);
                return provider.send(method, params);
            }
            const socket = await AuthServerProvider.getSocket();
            const identity = this.getIdentity();
            const requestResponse = await AuthServerProvider.createRequest(socket, {
                method,
                params,
                authChain: identity?.authChain
            });
            AuthServerProvider.openAuthDapp(requestResponse);
            const outcome = await AuthServerProvider.awaitOutcomeWithTimeout(socket, requestResponse);
            if (outcome.error) {
                throw outcome.error;
            }
            return outcome.result;
        };
        this.sendAsync = async (payload, callback) => {
            try {
                const result = await this.request(payload);
                callback(null, result);
            }
            catch (e) {
                if (isRPCError(e)) {
                    callback(e, { error: e, id: undefined, jsonrpc: '2.0' });
                }
                else {
                    callback({
                        code: 999,
                        message: isErrorWithMessage(e) ? e.message : 'Unknown error'
                    }, {
                        error: {
                            code: 0,
                            message: isErrorWithMessage(e) ? e.message : 'Unknown error'
                        },
                        id: undefined,
                        jsonrpc: '2.0'
                    });
                }
            }
        };
        this.deactivate = () => {
            AuthServerProvider.deactivate();
        };
        this.getIdentity = () => {
            const account = AuthServerProvider.getAccount();
            if (!account) {
                return null;
            }
            return sso.localStorageGetIdentity(account);
        };
    }
    /**
     * Set the url of the auth server to be consumed by this provider.
     */
    static setAuthServerUrl(url) {
        AuthServerProvider.authServerUrl = url;
    }
    /**
     * Set the url of the auth dapp to be consumed by this provider.
     */
    static setAuthDappUrl(url) {
        AuthServerProvider.authDappUrl = url;
    }
    /**
     * Set the time it will take for the identity created on 'finishSignIn' to expire.
     */
    static setIdentityExpiration(millis) {
        AuthServerProvider.identityExpirationInMillis = millis;
    }
    /**
     * Set the function that will be used to open the browser.
     */
    static setOpenBrowser(openBrowser) {
        AuthServerProvider.openBrowser = openBrowser;
    }
}
AuthServerProvider.authServerUrl = '';
AuthServerProvider.authDappUrl = '';
AuthServerProvider.identityExpirationInMillis = 30 * 24 * 60 * 60 * 1000; // 30 days in the future.
/**
 * Initializes the first part of the sign in process.
 * It returns data such as the request expiration as well as the verification code which can be used on the frontend.
 * The returned data should be passed back to the `finishSignIn` method.
 */
AuthServerProvider.initSignIn = async () => {
    const socket = await AuthServerProvider.getSocket();
    const ephemeralAccount = ethers.Wallet.createRandom();
    const expiration = new Date(Date.now() + AuthServerProvider.identityExpirationInMillis);
    const ephemeralMessage = Authenticator.getEphemeralMessage(ephemeralAccount.address, expiration);
    const requestResponse = await AuthServerProvider.createRequest(socket, {
        method: 'dcl_personal_sign',
        params: [ephemeralMessage]
    });
    return {
        socket,
        ephemeralAccount,
        expiration,
        ephemeralMessage,
        requestResponse
    };
};
/**
 * Should be called after the 'initSignIn' method with the same data it returned.
 * These methods are separate to allow the frontend to consume and display certain data before opening the auth dapp.
 */
AuthServerProvider.finishSignIn = async ({ socket, ephemeralAccount, expiration, ephemeralMessage, requestResponse }) => {
    AuthServerProvider.openAuthDapp(requestResponse);
    const { sender: signer, result: signature } = await AuthServerProvider.awaitOutcomeWithTimeout(socket, requestResponse);
    const identity = {
        expiration,
        ephemeralIdentity: {
            address: ephemeralAccount.address,
            privateKey: ephemeralAccount.privateKey,
            publicKey: ephemeralAccount.publicKey
        },
        authChain: [
            {
                type: AuthLinkType.SIGNER,
                payload: signer.toLowerCase(),
                signature: ''
            },
            {
                type: AuthLinkType.ECDSA_PERSONAL_EPHEMERAL,
                payload: ephemeralMessage,
                signature: signature
            }
        ]
    };
    localStorage.setItem(STORAGE_KEY_ADDRESS, signer.toLowerCase());
    sso.localStorageStoreIdentity(signer, identity);
};
/**
 * Get the persisted account from local storage.
 */
AuthServerProvider.getAccount = () => {
    return localStorage.getItem(STORAGE_KEY_ADDRESS);
};
/**
 * Clears the identity and the rest of the persisted data created by this provider.
 */
AuthServerProvider.deactivate = () => {
    const account = AuthServerProvider.getAccount();
    if (account) {
        sso.localStorageClearIdentity(account);
    }
    localStorage.removeItem(STORAGE_KEY_ADDRESS);
    localStorage.removeItem(STORAGE_KEY_CHAIN_ID);
};
/**
 * Check that the current account has a an identity that is not expired.
 */
AuthServerProvider.hasValidIdentity = () => {
    const account = AuthServerProvider.getAccount();
    if (!account) {
        return false;
    }
    // The sso function will not return an identity if it is expired.
    return !!sso.localStorageGetIdentity(account);
};
/**
 * Waits for an outcome message but times out if the expiration defined in the provided request is reached.
 */
AuthServerProvider.awaitOutcomeWithTimeout = async (socket, requestResponse) => {
    const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error('Timeout'));
        }, new Date(requestResponse.expiration).getTime() - Date.now());
    });
    const resultPromise = new Promise(resolve => {
        const onMessage = (msg) => {
            if (msg.requestId === requestResponse.requestId) {
                socket.off('message', onMessage);
                resolve(msg);
            }
        };
        socket.on('outcome', onMessage);
    });
    try {
        return await Promise.race([timeoutPromise, resultPromise]);
    }
    finally {
        socket.disconnect();
    }
};
/**
 * Opens the browser on the auth dapp requests page for the given request id.
 */
AuthServerProvider.openAuthDapp = (requestResponse) => {
    const url = `${AuthServerProvider.authDappUrl}/requests/${requestResponse.requestId}`;
    const target = '_blank';
    const features = 'noopener,noreferrer';
    AuthServerProvider.openBrowser
        ? AuthServerProvider.openBrowser(url, target, features)
        : window.open(url, target, features);
};
/**
 * Creates a socket connection and waits for it to be connected before returning it.
 */
AuthServerProvider.getSocket = async () => {
    const socket = io(AuthServerProvider.authServerUrl);
    await new Promise(resolve => {
        const onConnect = () => {
            socket.off('connect', onConnect);
            resolve();
        };
        socket.on('connect', onConnect);
    });
    return socket;
};
/**
 * Emits an event to create a request with a given payload and waits for the response.
 */
AuthServerProvider.createRequest = async (socket, payload) => {
    // TODO: Also send the chain id for requests that are not dcl_personal_sign once supported on the auth server.
    const response = await socket.emitWithAck('request', {
        method: payload.method,
        params: payload.params,
        authChain: payload.authChain
    });
    if (response.error) {
        socket.disconnect();
        throw new Error(response.error);
    }
    return response;
};
//# sourceMappingURL=AuthServerProvider.js.map